import axios, { AxiosPromise } from 'axios';
import { Promise } from 'es6-promise';
import { find } from 'lodash';
import * as Raven from 'raven-js';
import { addTranslationForLanguage, initialize, setActiveLanguage } from 'react-localize-redux';
import * as Redux from 'redux';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import * as BuildConstants from '../../BuildConstants';
import * as analytics from '../analytics';
import * as Api from '../Api';
import * as Basket from '../BasketControl';
import { SubmitTypeOptions } from '../component/Checkout/index.weborder';
import * as Data from '../Data';
import * as DeliveryLocations from '../DeliveryLocations';
import * as Intercom from '../intercom';
import * as OrderFlow from '../OrderFlow';
import { userActions } from '../redux';
import { setCookieConsent } from '../redux/cookie-consent/actions';
import { Routes } from '../Routes';
import { basketSelectors } from '../selectors';
import * as Store from '../Store';
import { Utilities } from '../Utilities';
import * as Vouchers from '../Vouchers';
import { clearBasket, removeFromBasket } from './basketActions';
import { getTranslationOrDefault } from '../../localization';
import { cookieConsent } from '../cookieconsent';

type ThunkResult<R> = ThunkAction<R, Store.State, undefined, Store.Action>;

const AddToBasket = (payload: Basket.Item): Store.AddToBasket => ({
  type: 'ADD_TO_BASKET',
  payload
});
const ClearBasket = clearBasket;
const RemoveFromBasket = removeFromBasket;
const SetTip = (payload: number): Store.SetTip => ({
  type: 'BASKET_SET_TIP',
  payload
});

export { AddToBasket, ClearBasket, RemoveFromBasket, SetTip };

export const ClearFlipdishStore = (): Store.ClearFlipdishStore => {
  return {
    type: 'CLEAR_FLIPDISH_STORE'
  };
};

export const PushAfterLogin = (url: string): Store.PushLoginUrl => {
  return {
    type: 'PUSH_LOGIN_URL',
    payload: url
  };
};

export const SetSmsEnabled = (state: boolean): Store.SetSmsEnabled => {
  return {
    type: 'SET_SMS_ENABLED',
    payload: state
  };
};

export const SetRequestedTime = (payload: {
  menuId: number;
  time: string;
}): Store.SetRequestedTime => {
  return {
    type: 'SET_REQUESTED_TIME',
    payload
  };
};

export const SetCardOrder = (cardOrder: Store.CardOrder | null): Store.SetCardOrder => {
  return {
    type: 'SET_CARD_ORDER',
    payload: cardOrder
  };
};

export const SetClientAnalytics = (clientAnalytics: boolean): Store.SetClientAnalytics => {
  return {
    type: 'SET_CLIENT_ANALYTICS',
    payload: clientAnalytics
  };
};

export const SetCanCancel = (seconds: number) => {
  return (dispatch: Redux.Dispatch<Redux.Action>, getState: () => Store.State) => {
    dispatch({
      type: 'SET_CAN_CANCEL',
      payload: true
    });

    setTimeout(() => {
      dispatch({
        type: 'SET_CAN_CANCEL',
        payload: false
      });

      //If order hasn't been cancelled till then send ecommerce details
      const order = getState().flipdish.Order;

      if (order.state === 'paid') {
        console.log(order.orderId, order.total, Api.Currency[order.currency]);
        analytics.EcommerceTransaction(order.orderId, order.total, Api.Currency[order.currency]);

        for (const item of order.basket.items) {
          analytics.EcommerceItem(
            order.orderId,
            item.Item.Name,
            item.Item.MenuItemId.toString(),
            item.Item.ActualPrice,
            1,
            Api.Currency[order.currency]
          );
        }

        analytics.EcommerceSend();
      }
    }, seconds * 1000);
  };
};

export const SetMenuScroll = (top: number): Store.SetMenuScroll => {
  return {
    type: 'SET_MENU_SCROLL',
    payload: top
  };
};

export const AddToast = (message: string) => {
  return (dispatch: Redux.Dispatch<Redux.Action>, getState: () => Store.State) => {
    const toastMessages = getState().flipdish.ToastMessages;

    const messageNotInToastMessages =
      toastMessages.findIndex((toastMessage) => toastMessage === message) === -1;

    if (messageNotInToastMessages) {
      dispatch({
        type: 'ADD_TOAST',
        payload: message
      });

      setTimeout(() => {
        dispatch({ type: 'REMOVE_TOAST' });
      }, 5000);
    }
  };
};

export const SaveAddressSuggestion = (suggestion: string, postalCode: any) => {
  console.log('SaveAddressSuggestion', suggestion);
  return (dispatch: Redux.Dispatch<Redux.Action>) => {
    dispatch({
      type: 'ADD_ADDRESS_SUGGESTION',
      payload: {
        suggestion,
        postalCode
      }
    });
  };
};

const AddDeliveryOption = (
  dl: Api.DeliveryLocation,
  option: Store.DeliveryOption
): Store.AddDeliveryOption => {
  return {
    type: 'ADD_DELIVERY_OPTION',
    payload: {
      dlid: dl.DeliveryLocationId,
      option
    }
  };
};

export const SetRestaurantUrl = (url: string): Store.SetRestaurantUrl => {
  return {
    type: 'SET_RESTAURANT_URL',
    payload: url
  };
};

export const SetCountryCode = (code: string) => ({
  type: 'SET_COUNTRY_CODE',
  payload: code
});

export const AddLocation = (
  location: Api.Geo,
  certainty: Store.LocationCertainty
): Store.AddLocation => {
  return {
    type: 'ADD_LOCATION',
    payload: {
      certainty,
      geo: location
    }
  };
};

export const InitializeExpandedState = (
  vrid: number,
  expandedState: number[]
): Store.InitializeExpandedState => {
  return {
    type: 'INITIALIZE_EXPANDED_STATE',
    payload: {
      vrid,
      expandedState
    }
  };
};

export const AddToExpandedState = (
  vrid: number,
  menuSectionId: number
): Store.AddToExpandedState => {
  return {
    type: 'ADD_TO_EXPANDED_STATE',
    payload: {
      menuSectionId,
      vrid
    }
  };
};

export const RemoveFromExpandedState = (
  vrid: number,
  menuSectionId: number
): Store.RemoveFromExpandedState => {
  return {
    type: 'REMOVE_FROM_EXPANDED_STATE',
    payload: {
      menuSectionId,
      vrid
    }
  };
};

export const EditChefNotes = (vrid: number, value: string): Store.EditChefNotes => {
  return {
    type: 'EDIT_CHEF_NOTES',
    payload: {
      value,
      vrid
    }
  };
};

export const EditPickupLocationTypes = (
  vrid: number,
  value: Api.PickupLocationType
): Store.EditPickupLocationTypes => {
  return {
    type: 'EDIT_PICKUP_LOCATION_TYPES',
    payload: {
      value,
      vrid
    }
  };
};

export const EditTableNumbers = (vrid: number, value: number): Store.EditTableNumbers => {
  return {
    type: 'EDIT_TABLE_NUMBERS',
    payload: {
      value,
      vrid
    }
  };
};

export const SetOrder = (order: OrderFlow.State): Store.SetOrder => {
  return {
    type: 'SET_ORDER',
    payload: order
  };
};

export const SetBounced = (bounced: boolean): Store.SetBounced => {
  return {
    type: 'SET_BOUNCED',
    payload: bounced
  };
};

export const SetTheme = (isDark: boolean): Store.SetTheme => {
  return {
    type: 'SET_THEME',
    payload: isDark
  };
};

export const SetOffset = (Offset: number): Store.SetOffset => {
  return {
    type: 'SET_OFFSET',
    payload: Offset
  };
};

export const SetLiveOptions = (options: Api.MenuItemOptionSetItem[]): Store.SetLiveOptions => {
  return {
    type: 'SET_LIVE_OPTIONS',
    payload: options
  };
};

export const SetNextOptions = (
  prevScreenOptions: Api.MenuItemOptionSetItem[][],
  current: number,
  prevIndices: number[]
): Store.SetNextOptions => {
  return {
    type: 'SET_NEXT_OPTIONS',
    payload: {
      PrevScreenOptions: prevScreenOptions,
      Current: current,
      PrevIndices: prevIndices
    }
  };
};

export const SetOptionsState = (
  options: Api.MenuItemOptionSetItem[],
  prevScreenOptions: Api.MenuItemOptionSetItem[][],
  current: number,
  prevIndices: number[]
): Store.SetOptionsState => {
  return {
    type: 'SET_OPTIONS_STATE',
    payload: {
      Options: options,
      PrevScreenOptions: prevScreenOptions,
      Current: current,
      PrevIndices: prevIndices
    }
  };
};

const OrderSubmissionFailure = (
  dispatch: Redux.Dispatch<Redux.Action>,
  getState: () => Store.State,
  errorMsg: string,
  error: any,
  vrid: number,
  basket: Basket.Data,
  submitType: OrderFlow.SubmitOrderType,
  paymentAccount?: Api.PaymentAccount,
  orderId?: number
) => {
  let appconfig: any = {};
  let customer: any = {};
  let paymentaccount: any = {};

  const flipdishAppConfig = getState().flipdish.AppConfig;
  const flipdishCustomer = getState().flipdish.Customer;

  if (flipdishAppConfig.status === 'success') {
    appconfig.AppNameId = flipdishAppConfig.data.AppNameId;
    appconfig.WhiteLabelId = flipdishAppConfig.data.WhiteLabelId;
  }

  if (flipdishCustomer.status === 'success') {
    customer.CustomerName = flipdishCustomer.data.CustomerName;
    customer.EmailAddress = flipdishCustomer.data.EmailAddress;
  }

  if (paymentAccount) {
    paymentaccount.PaymentAccountId = paymentAccount.PaymentAccountId;
    paymentaccount.PaymentAccountType = paymentAccount.PaymentAccountType;
    paymentaccount.Description = paymentAccount.Description;
  }

  Raven.captureMessage(errorMsg, {
    tags: { category: 'API failure' },
    extra: {
      error: Utilities.handleError(error),
      appconfig: appconfig,
      customer: customer,
      vrid: vrid,
      orderid: orderId || null,
      paymentaccount: paymentaccount,
      basket: Utilities.trimBasket(basket)
    }
  });

  let message: string =
    'We are sorry - an internal error occurred when placing your order. Please try again later or call the restaurant to place your order.';

  if (error.response) {
    const result = error.response.data;

    if (result.UserMessage) {
      message = result.UserMessage;
    }
  }

  dispatch(
    SetOrder({
      state: 'failed',
      submitType,
      message
    })
  );
};

const MaybeAddVoucher = (
  voucherCode: string,
  orderId: number,
  paymentAccountId: number | null,
  basket: Basket.Data,
  options?: SubmitTypeOptions
): Promise<any> => {
  // only apply if user overwritten auto-apply voucher
  const isUserInput = basket.voucher.status === 'valid' && basket.voucher.isUserInput;

  if (
    !isUserInput ||
    !voucherCode ||
    (options && options.paypal && !options.paypal.useSavedAccount) ||
    !paymentAccountId
  ) {
    return Promise.resolve(true);
  }
  return axios.post('/order/ApplyVoucherCodeToOrder', {
    orderId,
    voucherCode,
    paymentAccountId
  }) as any;
};

const SubmitPaymentForOrder = (
  vrid: number,
  account: Api.PaymentAccount,
  orderId: number,
  voucherCode: string,
  chefNote: string,
  basket: Basket.Data,
  currency: Api.Currency,
  total: number,
  type?: OrderFlow.SubmitOrderType,
  options?: SubmitTypeOptions,
  concessionStoreId?: number
) => {
  return (dispatch: ThunkDispatch<Store.State, {}, Redux.Action>, getState: () => Store.State) => {
    dispatch(
      SetOrder({
        state: 'created',
        submitType: type,
        orderId,
        basket,
        chefNote,
        vrid,
        currency,
        total
      })
    );

    const accountId = account && account.PaymentAccountId ? account.PaymentAccountId : null;

    const addVoucher = MaybeAddVoucher(voucherCode, orderId, accountId, basket, options);

    addVoucher.catch((e) =>
      OrderSubmissionFailure(
        dispatch,
        getState,
        'Add voucher to order failure',
        e,
        vrid,
        basket,
        type,
        account,
        orderId
      )
    );

    addVoucher.then(() => {
      if (!window.location.origin) {
        // For IE
        // @ts-ignore
        window.location.origin =
          window.location.protocol +
          '//' +
          (window.location.port ? ':' + window.location.port : '');
      }

      const refererUrl = window.location.origin + window.location.pathname;

      let xhr;

      if (type === 'STRIPE' && options.googleApplePayStripeToken) {
        xhr = axios.post('/order/submitorderpaybutton', {
          orderId,
          stripePaymentMethodId: options.googleApplePayStripeToken,
          chefNote,
          refererUrl
        });
      } else if (type === 'PAYPAL') {
        // Use PaymentAccountId from PayPal PyamentAccoun if it exists
        if (options.paypal.useSavedAccount) {
          xhr = axios.post('/order/submitorder', {
            orderId,
            paymentAccountId: account.PaymentAccountId,
            chefNote,
            refererUrl
          });
        } else {
          xhr = axios.post('/order/submitorderpaypal', {
            orderId,
            clientToken: options.paypal.nonce,
            deviceData: options.paypal.deviceData,
            chefNote,
            refererUrl
          });
        }
      } else {
        xhr = axios.post('/order/submitorder', {
          orderId,
          paymentAccountId: account.PaymentAccountId,
          chefNote,
          refererUrl
        });
      }

      xhr.then((resp) => {
        console.log('Paid + order submitted', resp);

        if (resp.data.Data.RedirectUri) {
          //If redirect first track analytics
          analytics.EcommerceTransaction(orderId, total, Api.Currency[currency]);

          for (const item of basket.items) {
            analytics.EcommerceItem(
              orderId,
              item.Item.Name,
              item.Item.MenuItemId.toString(),
              item.Item.ActualPrice,
              1,
              Api.Currency[currency]
            );
          }

          analytics.EcommerceSend();

          //And redirect
          window.location.href = resp.data.Data.RedirectUri;
        } else {
          const goto = concessionStoreId
            ? `/order/${orderId}/${concessionStoreId}`
            : `/order/${orderId}`;
          Utilities.navigateTo(goto);

          // use OrderResponse: resp.data.Data, ?
          dispatch(
            SetOrder({
              state: 'paid',
              submitType: type,
              orderId,
              basket,
              chefNote,
              vrid,
              currency,
              total
            })
          );

          const dispatchThunk = dispatch as any;

          dispatchThunk(SetCanCancel(15));
        }
      });

      xhr.catch((e) => {
        OrderSubmissionFailure(
          dispatch,
          getState,
          'Order submission failure',
          e,
          vrid,
          basket,
          type,
          account,
          orderId
        );
      });
    });
  };
};

export const IncrementVouchersInProgress = (): Store.IncrementVouchersInProgress => {
  return {
    type: 'INCREMENT_VOUCHERS_IN_PROGRESS'
  };
};

export const DecrementVouchersInProgress = (): Store.DecrementVouchersInProgress => {
  return {
    type: 'DECREMENT_VOUCHERS_IN_PROGRESS'
  };
};

export const AddVoucherToOrder = (
  route: Routes,
  voucherCode: string,
  paymentAccountId: number,
  menuId: number,
  versionGuid: string
) => {
  // console.log('add voucher', route, voucherCode, paymentAccountId);

  return (dispatch: Redux.Dispatch<Redux.Action>, getState: () => Store.State) => {
    const state = getState();

    if (!route.params.type && !route.params.vrid) {
      return;
    }

    const deliveryType = route.params.type;
    const isDelivery = deliveryType === 'delivery';
    const vrId = route.params.vrid;
    const prId = !isDelivery ? route.params.id : undefined;
    const deliveryLocationId = isDelivery ? route.params.id : undefined;

    const orderPayload = basketSelectors.getOrderPayload(state, {
      menuId,
      menuVersionGuid: versionGuid,
      deliveryType: route.params.type,
      vrId,
      prId,
      deliveryLocationId
    });

    const setVoucher = (v: Vouchers.States) => {
      dispatch({
        type: 'SET_VOUCHER',
        payload: {
          voucher: v,
          menuId
        }
      });
    };

    if (!orderPayload || typeof orderPayload === 'string') {
      console.warn('[BASKET] AddVoucherToOrder ORDER_PAYLOAD_ERROR', orderPayload);
      setVoucher({
        status: 'none',
        code: ' '
      });
      dispatch({
        type: 'DECREMENT_VOUCHERS_IN_PROGRESS'
      });
      return;
    }

    setVoucher({
      status: 'loading',
      code: ' ',
      items: []
    });

    const errorHandler = (axiosError: any) => {
      const message = Utilities.getUserMessage(axiosError);
      setVoucher({
        status: 'none',
        code: ' '
      });
      dispatch({
        type: 'DECREMENT_VOUCHERS_IN_PROGRESS'
      });
      analytics.Track('Orders', 'Add Voucher Failure');
      const dispatchThunk = dispatch as any;
      dispatchThunk(AddToast(message));
    };

    // Dont send paymentAccountId here ever
    // Because it will try and auto-apply a voucher to the new order.
    // We want to be able to call ApplyVoucherCodeToOrder oursleves to choose the voucher applied
    const xhr = axios.post('/order/order', { ...orderPayload });
    xhr.catch(errorHandler);

    xhr.then((result) => {
      const orderId = result.data.Data.OrderId;

      const applyCode = Utilities.toPromise<Api.VoucherBasketItemDm[]>(
        axios.post('/order/ApplyVoucherCodeToOrder', {
          orderId,
          voucherCode,
          paymentAccountId
        })
      );
      applyCode.catch(errorHandler);
      applyCode.then((voucher) => {
        if (voucher[0]) {
          analytics.Track('Orders', 'Add Voucher Success', {
            eventLabel: voucher[0].Description
          });
        }

        dispatch({
          type: 'DECREMENT_VOUCHERS_IN_PROGRESS'
        });

        setVoucher({
          items: voucher,
          code: voucherCode,
          status: 'valid'
        });
      });
    });
  };
};

export const SubmitOrder = (
  order: Api.DeliveryOrder | Api.CollectionOrder,
  vrid: number,
  account: Api.PaymentAccount,
  voucherCode: string,
  chefNote: string,
  basket: Basket.Data,
  currency: Api.Currency,
  total: number,
  type?: OrderFlow.SubmitOrderType,
  options?: SubmitTypeOptions,
  concessionStoreId?: number
) => {
  return (dispatch: Redux.Dispatch<Redux.Action>, getState: () => Store.State) => {
    const dispatchThunk = dispatch as any;

    dispatch(
      SetOrder({
        state: 'creating',
        submitType: type
      })
    );

    let xhr: AxiosPromise<any>;
    if (basket.voucher.status === 'valid' && basket.voucher.isUserInput) {
      const noVoucherAutoApplyOrderPayload = {
        ...order,
        PaymentAccountId: undefined
      };
      xhr = axios.post('/order/order', noVoucherAutoApplyOrderPayload);
    } else {
      xhr = axios.post('/order/order', order);
    }

    xhr.then((result) => {
      const orderId: number = result.data.Data.OrderId;

      console.log('Order submit success. ID=' + orderId, result);

      dispatch(
        SetOrder({
          state: 'created',
          submitType: type,
          orderId,
          basket,
          chefNote,
          vrid,
          currency,
          total
        })
      );

      dispatchThunk(
        SubmitPaymentForOrder(
          vrid,
          account,
          orderId,
          voucherCode,
          chefNote,
          basket,
          currency,
          total,
          type,
          options,
          concessionStoreId
        )
      );
    });

    xhr.catch((e) =>
      OrderSubmissionFailure(dispatch, getState, 'Order creation failed', e, vrid, basket, type)
    );
  };
};

export const setCollectionPreOrderTimes = (payload: {
  vrId: number;
  prId: number;
  preOrderTimes: Api.PreOrderTime[];
}) => {
  return {
    type: 'SET_RESTAURANT_COLLECTION_PRE_ORDER_TIMES',
    payload
  };
};

/*refresh argument is there when the collection details need to be refreshed (but not the menu).
This is required when changing the language as the language of the times change too and thus new data is required from the API*/
const LoadCollectionDetails = (prid: number, refresh: boolean) => {
  return (dispatch: Redux.Dispatch<Redux.Action>, getState: () => Store.State) => {
    const dis = (r: Data.Async<Api.CollectionRestaurantDetail>) => {
      dispatch({
        type: 'SET_RESTAURANT_COLLECTION',
        payload: {
          restaurant: r,
          prid
        }
      });
    };

    const Restaurant = getState().collectionRestaurants[prid];

    //Work with the cached version
    const pickupRestaurant = localStorage.getItem('flipdish-pickup-restaurant-' + prid);
    const pickupRestaurantJson = JSON.parse(pickupRestaurant);
    const restaurantExists = Restaurant && Restaurant.status === 'success';

    /*Get from localStorage if it exists in localStorage, is not in redux store
    and is on an initial pass (hence new data from API is not required)*/
    if (pickupRestaurantJson && !restaurantExists && !refresh) {
      const detail: Api.CollectionRestaurantDetail = pickupRestaurantJson;
      detail.FromAPI = false;
      dis({ status: 'success', data: detail });
    }

    const req = axios.get('/Restaurant/PickupRestaurantDetails/' + prid);

    req.catch((e) => {
      let appconfig: any = {};

      const flipdishAppConfig = getState().flipdish.AppConfig;

      if (flipdishAppConfig.status === 'success') {
        appconfig.AppNameId = flipdishAppConfig.data.AppNameId;
        appconfig.WhiteLabelId = flipdishAppConfig.data.WhiteLabelId;
      }

      // sentry-disable-quota-overflow
      // Raven.captureMessage('Error loading collection restaurant', {
      //   tags: { category: 'API failure' },
      //   extra: {
      //     error: Utilities.handleError(e),
      //     appconfig: appconfig,
      //     prid: prid
      //   }
      // });
      if (!pickupRestaurant) {
        dis({ status: 'none' });
      }
    });

    req.then((r) => {
      const detail: Api.CollectionRestaurantDetail = r.data.Data;
      //Store Pickup Restaurant Details in localStorage
      Utilities.setInLocalStorage('flipdish-pickup-restaurant-' + prid, JSON.stringify(detail));

      detail.FromAPI = true;
      dis({
        status: 'success',
        data: detail
      });
    });
  };
};

export const setDeliveryPreOrderTimes = (payload: {
  vrId: number;
  dlId: number;
  preOrderTimes: Api.PreOrderTime[];
}) => {
  return {
    type: 'SET_RESTAURANT_DELIVERY_PRE_ORDER_TIMES',
    payload
  };
};

export const LoadDeliveryDetails = (vrid: number, deliveryLocationId: number, refresh = false) => {
  return (dispatch: Redux.Dispatch<Redux.Action>, getState: () => Store.State) => {
    const dis = (r: Data.Async<Api.DeliveryRestaurantDetail>) => {
      dispatch({
        type: 'SET_RESTAURANT_DELIVERY',
        payload: {
          restaurant: r,
          vrid,
          dlid: deliveryLocationId
        }
      });
    };

    const key = Store.DeliveryKey(vrid, deliveryLocationId);
    const Restaurant = getState().deliveryRestaurants[key];

    const restaurantExistsFromApi = Restaurant && Restaurant.status === 'success';

    if (restaurantExistsFromApi && !refresh) {
      return;
    }

    // TODO:
    // Assert dl has a valid integer ID.
    // Assert valid device ID
    const query = {
      deviceId: getState().flipdish.Config.DeviceId,
      deliveryLocationId
    };

    const req = axios.get('/Restaurant/DeliveryRestaurantDetails/' + vrid, {
      params: query
    });

    req.catch((e) => {
      let appconfig: any = {};

      const flipdishAppConfig = getState().flipdish.AppConfig;

      if (flipdishAppConfig.status === 'success') {
        appconfig.AppNameId = flipdishAppConfig.data.AppNameId;
        appconfig.WhiteLabelId = flipdishAppConfig.data.WhiteLabelId;
      }

      Raven.captureMessage('Error loading delivery restaurant', {
        tags: { category: 'API failure' },
        extra: {
          error: Utilities.handleError(e),
          appconfig: appconfig,
          vrid: vrid,
          deliveryLocationId: deliveryLocationId
        }
      });

      dis({ status: 'none' });
    });

    req.then((r) => {
      const detail: Api.DeliveryRestaurantDetail = r.data.Data;
      if (detail) {
        detail.FromAPI = true;
        dis({ status: 'success', data: detail });
      }
    });
  };
};

export const getExtraMenu = (menuUrl: string) => {
  return (dispatch: Redux.Dispatch<Redux.Action>, getState: () => Store.State) => {
    Utilities.loadMenuByMenuUrl(menuUrl, dispatch, getState);
  };
};

export const LoadRestaurant = (props: Routes) => {
  const { params } = props;

  const vrId = parseInt(`${params.vrid}`);
  const deliveryType = params.type;

  return (dispatch: Redux.Dispatch<Redux.Action>, getState: () => Store.State) => {
    const dispatchThunk = dispatch as any;

    const { CollectionSummaries, DeliverySummaries } = getState().flipdish;

    let Summary: Api.RestaurantSummaryCommon;
    switch (deliveryType) {
      case 'collection': {
        const prId = parseInt(`${params.id}`);
        if (CollectionSummaries.status === 'success' && prId) {
          Summary = CollectionSummaries.data.find(
            (s) => s.VirtualRestaurantId === vrId && s.PhysicalRestaurantId === prId
          );

          dispatchThunk(LoadCollectionDetails(prId, false));
        }
        break;
      }
      case 'delivery': {
        const deliveryLocationId = parseInt(`${params.id}`);
        if (DeliverySummaries.status === 'success' && deliveryLocationId) {
          Summary = DeliverySummaries.data.find((s) => s.VirtualRestaurantId === vrId);

          dispatchThunk(LoadDeliveryDetails(vrId, deliveryLocationId, true));
        }
        break;
      }
      default:
        // why this!!! idk but was like this before
        const getSummary = (summaries: Data.Async<Api.RestaurantSummaryCommon[]>) => {
          return (
            summaries.status === 'success' &&
            summaries.data.find((s) => s.VirtualRestaurantId === vrId)
          );
        };
        if (CollectionSummaries.status === 'success' && CollectionSummaries.data.length > 0) {
          Summary = getSummary(CollectionSummaries);
        } else if (DeliverySummaries.status === 'success') {
          Summary = getSummary(DeliverySummaries);
        }
        break;
    }

    //If summary load menu
    if (Summary) {
      Utilities.loadMenu([Summary], dispatch, getState, false);
    }
  };
};

export const LoadAppconfig = () => {
  return (dispatch: Redux.Dispatch<Redux.Action>) => {
    const dispatchThunk = dispatch as any;

    dispatchThunk(GeoIpLookup());

    //Work with the cached version
    const cachedConfig = localStorage.getItem('flipdish-app-config');

    const generateCSS = (Primary: string, Secondary: string, Tertiary: string) => {
      const styleRules = [];

      styleRules.push(
        `.fd-typeahead>.form-control{background:${Primary}!important;color:${Tertiary}!important;}`
      );
      styleRules.push(`.fd-typeahead>.form-control.focus{border-color:${Secondary}!important;}`);
      styleRules.push(
        `.dark .fd-typeahead>.form-control{background:${Tertiary}!important;color:${Primary}!important;border-color:${Primary}!important;}`
      );
      styleRules.push(
        `.dark .fd-typeahead>.form-control.focus{border-color:${Primary}!important;}`
      );
      styleRules.push(`.dropdown-menu{background-color:${Primary}!important;}`);
      styleRules.push(`.dropdown-menu>li>a{color:${Tertiary}!important;}`);
      styleRules.push(`.dark .dropdown-menu{background-color:${Tertiary}!important;}`);
      styleRules.push(`.dark .dropdown-menu>li>a{color:${Primary}!important;}`);

      const style = document.createElement('style');
      style.type = 'text/css';
      style.innerHTML = styleRules.join('\n');
      document.getElementsByTagName('head')[0].appendChild(style);
    };

    const getFont = (Font: string) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = `https://fonts.googleapis.com/css?family=${Font.replace(' ', '+')}:400,400i,700`;
      document.getElementsByTagName('head')[0].appendChild(link);

      const style = document.createElement('style');
      style.type = 'text/css';
      style.innerHTML = `#flipdish-menu .dropdown-menu, #flipdish-menu .form-control, #flipdish-menu .h1, #flipdish-menu .h2, #flipdish-menu .h3, #flipdish-menu .h4, #flipdish-menu .panel-heading, #flipdish-menu .well, #flipdish-menu input, #flipdish-menu textarea, #flipdish-menu a, #flipdish-menu h1, #flipdish-menu h2, #flipdish-menu h3, #flipdish-menu h4, #flipdish-menu li, #flipdish-menu p, #flipdish-menu span {
        font-family: "${Font}", "sans-serif";
      }`;
      document.getElementsByTagName('head')[0].appendChild(style);
    };

    if (cachedConfig) {
      try {
        const cachedConfigJson = JSON.parse(cachedConfig);

        if (cachedConfigJson.AppDesignScheme) {
          const AppDesignScheme = cachedConfigJson.AppDesignScheme;

          const Primary = Utilities.returnColor(
            AppDesignScheme.Tertiary,
            BuildConstants.PRIMARY_COLOR
          );

          const Secondary = Utilities.returnColor(
            AppDesignScheme.Primary,
            BuildConstants.THEME_COLOR
          );

          const Tertiary = Utilities.returnColor(
            AppDesignScheme.Secondary,
            BuildConstants.TERTIARY_COLOR
          );

          const Font = AppDesignScheme.Font || 'Roboto';

          generateCSS(Primary, Secondary, Tertiary);
          if (Font) {
            getFont(Font);
          }

          dispatch({
            type: 'SET_APP_DESIGN_SCHEME',
            payload: {
              Primary: Primary,
              Secondary: Secondary,
              Tertiary: Tertiary,
              // BuildConstants.FONT doesn't exists coz deploy.sh removing it
              Font
            }
          });
        }
        if (cachedConfigJson.Languages) {
          const languages = cachedConfigJson.Languages.map((lang: Api.Language) => {
            return lang.LangageId;
          });

          //TODO: Check if localization file is not there
          dispatchThunk(initialize(languages, { renderInnerHtml: false }));

          const currentLanguageInStorage = localStorage.getItem('flipdish-current-language');

          for (const language of languages) {
            const [localeCode, langJsonWithFallback] = getTranslationOrDefault(language);

            dispatchThunk(
              addTranslationForLanguage(
                Utilities.translateTransform(langJsonWithFallback),
                localeCode
              )
            );

            //If there is a language in the localStorage and it is in the list of allowed langauges set it as active
            if (currentLanguageInStorage && localeCode === currentLanguageInStorage) {
              dispatchThunk(setActiveLanguage(localeCode));
            }
          }
        }

        dispatchThunk(
          AddLocation(
            {
              Latitude: cachedConfigJson.MapCenterLatitude,
              Longitude: cachedConfigJson.MapCenterLongitude
            },
            Store.LocationCertainty.AppConfig
          )
        );

        dispatch({
          type: 'LOAD_APP_CONFIG',
          payload: cachedConfigJson
        });
      } catch (error) {}
    }

    const load = axios.get('/Account/AppConfig');
    load.catch((e) => {
      Raven.captureMessage('Error downloading app config', {
        tags: { category: 'API failure' },
        extra: {
          error: Utilities.handleError(e),
          appnameid: Utilities.extraOption('data-restaurant') || ''
        }
      });
      //alert('There was an error loading this restaurants configuration. Please refresh the page and try again.');
    });

    load.then((data) => {
      let config: Api.AppConfig = data.data.Data;
      console.log('Loaded App config', config);

      // Set legal docs urls
      const CookiePolicyUrl =
        Utilities.extraOption('data-cookie-policy-link') ||
        config.CookiePolicyUrl ||
        'https://www.flipdish.com/cookie-policy/';
      const TermsAndConditionsUrl =
        Utilities.extraOption('data-terms-link') ||
        config.TermsAndConditionsUrl ||
        'https://www.flipdish.com/docs/tac/';
      const PrivacyPolicyUrl =
        Utilities.extraOption('data-privacy-policy-link') ||
        config.PrivacyPolicyUrl ||
        'https://www.flipdish.com/privacy-policy/';

      config = { ...config, CookiePolicyUrl, TermsAndConditionsUrl, PrivacyPolicyUrl };

      // MOCK: Cookie consent
      // config.Features = [...config.Features, 'enable-gdpr-cookies'];

      //Store appconfig in localStorage
      Utilities.setInLocalStorage('flipdish-app-config', JSON.stringify(config));

      const AppDesignScheme = data.data.Data.AppDesignScheme;

      const Primary = Utilities.returnColor(AppDesignScheme.Tertiary, BuildConstants.PRIMARY_COLOR);

      const Secondary = Utilities.returnColor(AppDesignScheme.Primary, BuildConstants.THEME_COLOR);

      const Tertiary = Utilities.returnColor(
        AppDesignScheme.Secondary,
        BuildConstants.TERTIARY_COLOR
      );

      const Font = AppDesignScheme.Font || 'Roboto';

      generateCSS(Primary, Secondary, Tertiary);

      if (Font) {
        getFont(Font);
      }

      dispatch({
        type: 'SET_APP_DESIGN_SCHEME',
        payload: {
          Primary: Primary,
          Secondary: Secondary,
          Tertiary: Tertiary,
          // BuildConstants.FONT doesn't exists coz deploy.sh removing it
          Font
        }
      });

      const languages = data.data.Data.Languages.map((lang: Api.Language) => {
        return lang.LangageId;
      });

      //TODO: Check if localization file is not there
      dispatchThunk(initialize(languages, { renderInnerHtml: false }));

      const currentLanguageInStorage = localStorage.getItem('flipdish-current-language');
      let activeLang: string;
      for (const language of languages) {
        const [localeCode, langJsonWithFallback] = getTranslationOrDefault(language);

        dispatchThunk(
          addTranslationForLanguage(Utilities.translateTransform(langJsonWithFallback), localeCode)
        );

        //If there is a language in the localStorage and it is in the list of allowed langauges set it as active
        if (currentLanguageInStorage && localeCode === currentLanguageInStorage) {
          dispatchThunk(setActiveLanguage(localeCode));
          activeLang = localeCode;
        }
      }

      dispatchThunk(
        AddLocation(
          {
            Latitude: config.MapCenterLatitude,
            Longitude: config.MapCenterLongitude
          },
          Store.LocationCertainty.AppConfig
        )
      );

      dispatch({
        type: 'LOAD_APP_CONFIG',
        payload: config
      });

      const disableNonEssentialCookies = config.Features.includes('disable-non-essential-cookies');
      const fdCcEnabledFF = config.Features.includes('enable-gdpr-cookies');
      const setupGA = (disable) =>
        analytics.initialize(
          config.GoogleAnalyticsTokens.map((t) => ({ trackingId: t })),
          disable
        );

      if (disableNonEssentialCookies) {
        setupGA(disableNonEssentialCookies);
      } else if (fdCcEnabledFF) {
        const restaurantName = config.BrandName;
        const logoUrl = config.BrandLogoUrl;

        // Cookie consent modal
        cookieConsent.init(
          {
            lang: activeLang || 'en',
            logoUrl,
            restaurantName,
            cookiePolicyUrl: config.CookiePolicyUrl || ''
          },
          (selected) => {
            const isFunctionalEnabled = selected.includes('functional');
            const isPerformanceEnabled = selected.includes('performance');
            const isAdvertisingEnabled = selected.includes('advertising');

            // Facebook
            if (config.FacebookTokens.length > 0) {
              if (window['FB']) {
                analytics.setFBTracking(isAdvertisingEnabled);
              } else if (isAdvertisingEnabled) {
                analytics.InitFacebook(config.FacebookTokens[0]);
              }
            }

            // Google Analytics
            if (window['ga']) {
              analytics.setGATracking(isPerformanceEnabled);
            } else if (isPerformanceEnabled) {
              setupGA(disableNonEssentialCookies);
            }

            dispatch(setCookieConsent(selected as WebOrder.State.CookieConsent['selectedGroups']));
          }
        );
      } else {
        if (config.IntercomAppId) {
          //Intercom.Load(config.IntercomAppId, getState().intercom);
        }

        if (config.FacebookTokens.length > 0) {
          analytics.InitFacebook(config.FacebookTokens[0]);
        }

        setupGA(disableNonEssentialCookies);
      }

      //If client analytics is not present it will still set ClientAnalytics
      //to true so GA sends event's to Flipdish's Google Analytics
      dispatchThunk(SetClientAnalytics(true));
    });
  };
};

export const ChangeLanguage = (lang: string) => {
  return (dispatch: Redux.Dispatch<Redux.Action>, getState: () => Store.State) => {
    //Optimistic update
    const dispatchThunk = dispatch as any;
    dispatchThunk(setActiveLanguage(lang));

    Utilities.setInLocalStorage('flipdish-current-language', lang);

    //Add lang to headers
    axios.defaults.headers.common['Flipdish-Language'] = lang;

    const xhr = axios.put('/account/language/', {
      id: lang
    });

    xhr.then(() => {
      // @ts-ignore
      if (window.flipdishLanguageSet) {
        // @ts-ignore
        window.flipdishLanguageSet(lang);
      }

      //Once language changed reload restaurants as times come from the backend
      const deliveryLocations = getState().flipdish.DeliveryLocations;

      if (deliveryLocations.status === 'success') {
        deliveryLocations.data.forEach((dl) => {
          dispatchThunk(LoadDeliveryOptions(dl, false));
        });
      }

      const collectionSummaries = getState().flipdish.CollectionSummaries;
      if (collectionSummaries.status === 'success') {
        collectionSummaries.data.forEach((r: any) => {
          dispatchThunk(LoadCollectionDetails(r.PhysicalRestaurantId, true));
        });
      }
    });

    xhr.catch((e) => {
      console.log('Language change failed', e);
    });
  };
};

export const FindInitialRestaurant = (then?: (vrid: number) => any, iteration?: number) => {
  return (dispatch: Redux.Dispatch<Redux.Action>, getState: () => Store.State) => {
    const dispatchThunk = dispatch as any;

    const {
      Location: { geo },
      Config,
      AppConfig
    } = getState().flipdish;

    const q = {
      Latitude: geo.Latitude,
      Longitude: geo.Longitude,
      skip: 0,
      count: 1000
    };

    const extractMenus = (data: Api.RestaurantSummaryCommon[]) => {
      if (data.length === 0) {
        return;
      }

      if (then) {
        const vrid = data[0].VirtualRestaurantId;
        then(vrid);
      }
    };

    //Work with the cached version
    /*const physicalRestaurants = localStorage.getItem(
      'flipdish-physical-restaurants'
    );*/

    const promise = Utilities.toPromise<Api.CollectionRestaurantSummary[]>(
      axios.get('/Restaurant/PickupPhysicalRestaurantSummariesFromCoordinates', { params: q })
    );
    promise.catch((error) => {
      dispatch({
        type: 'SET_COLLECTION_SUMMARIES',
        payload: {
          status: 'none'
        }
      });
    });
    promise.then((data) => {
      dispatch({
        type: 'SET_COLLECTION_SUMMARIES',
        payload: {
          status: 'success',
          data
        }
      });

      if (AppConfig.status === 'success' && AppConfig.data.PickupEnabled) {
        data.forEach((r) => {
          dispatchThunk(LoadCollectionDetails(r.PhysicalRestaurantId, false));
        });
      }

      extractMenus(data);
      Utilities.loadMenu(data, dispatch, getState, false, iteration);
    });

    if (AppConfig.status === 'success' && AppConfig.data.DeliveryEnabled) {
      const deliveryParams = {
        Latitude: geo.Latitude,
        Longitude: geo.Longitude,
        skip: 0,
        count: 1000,
        deviceId: Config.DeviceId
      };
      Utilities.toPromise<Api.DeliveryRestaurantSummary[]>(
        axios.get('/Restaurant/DeliveryRestaurantSummaries', {
          params: deliveryParams
        })
      )
        .then((deliveryData) => {
          /*Utilities.setInLocalStorage(
          'flipdish-delivery-restaurant-summaries',
          JSON.stringify(deliveryData)
        );*/

          dispatch({
            type: 'SET_DELIVERY_SUMMARIES',
            payload: {
              status: 'success',
              data: deliveryData
            }
          });

          extractMenus(deliveryData);
          Utilities.loadMenu(deliveryData, dispatch, getState, false, iteration);
        })
        .catch((error) => {
          dispatch({
            type: 'SET_DELIVERY_SUMMARIES',
            payload: {
              status: 'none'
            }
          });
        });
    }
  };
};

export const GeoIpLookup = () => {
  // Thunk
  return (dispatch: Redux.Dispatch<Redux.Action>) => {
    const isOperaMini = navigator.userAgent.indexOf('Opera Mini') > -1;

    if (isOperaMini) {
      return;
    }

    interface IpInfo {
      ip: string;
      hostname: string;
      city: string;
      region: string;
      country: string;
      loc: string;
      org: string;
    }

    const commonHeaders = { ...axios.defaults.headers.common };

    delete commonHeaders['X-Flipdish-Token'];
    delete commonHeaders['X-Coordinates'];
    delete commonHeaders['Flipdish-App-Type'];
    delete commonHeaders['Flipdish-White-Label-Id'];

    const query = axios.get('/json', {
      baseURL: '//ipinfo.io',
      withCredentials: false,
      headers: {
        common: commonHeaders
      }
    });

    query.then((data) => {
      const result: IpInfo = data.data;
      const [lat, lng] = result.loc.split(',').map(parseFloat);

      const geo = { Latitude: lat, Longitude: lng };
      dispatch(SetCountryCode(result.country));
      dispatch(AddLocation(geo, Store.LocationCertainty.GeoIp));
    });
    query.catch(() => {
      console.error('Error looking up ip address on ipfinfo.io');
    });
  };
};

const loadDeliveryOptionsRequestTracker = new Set<string>();
const LoadDeliveryOptions = (dl: Api.DeliveryLocation, initial: boolean) => {
  return (dispatch: Redux.Dispatch<Redux.Action>, getState: () => Store.State) => {
    const { Config, AppConfig } = getState().flipdish;

    const q = {
      Latitude: dl.Coordinates.Latitude,
      Longitude: dl.Coordinates.Longitude,
      skip: 0,
      count: 1000,
      deviceId: Config.DeviceId,
      deliveryLocationId: dl.DeliveryLocationId
    };
    const trackerKey = JSON.stringify(q);

    // prevent 4+ extra unnecessary requests from who knows where
    if (loadDeliveryOptionsRequestTracker.has(trackerKey)) {
      return;
    }
    if (
      getState().deliveryOptions[q.deliveryLocationId] &&
      getState().deliveryOptions[q.deliveryLocationId].status === 'success'
    ) {
      return;
    }

    loadDeliveryOptionsRequestTracker.add(trackerKey);

    const query = Utilities.toPromise<Api.DeliveryRestaurantSummary[]>(
      axios.get('/Restaurant/DeliveryRestaurantSummaries', { params: q })
    );

    query.finally(() => {
      loadDeliveryOptionsRequestTracker.delete(trackerKey);
    });

    query.then((restaurants) => {
      if (AppConfig.status != 'success') {
        Raven.captureMessage('Cant load delivery options before app config', {
          tags: { category: 'Frontend error' },
          extra: {
            appnameid: Utilities.extraOption('data-restaurant') || ''
          }
        });

        return;
      }

      /**
       * BUG WE R NOT CHECKING IF MENU IS LOADED... DOES IT NEEDS TO BE LOADED...
       */
      const skipTo = find(restaurants, (r) => r.SkipToThisVirtualRestaurant);
      /* SUSPICIOUS
        DisplayDeliveryRestaurantListScreen | skipTo      | choiceOf  | result
        true                                | undefined   | [...]     | choice
        true                                | {...}       | [...]     | choice
        false                               | {...}       | [...]     | skip
        false                               | undefined   | [...]     | choice
        false                               | undefined   | [1]       | choice - but should it be skip?
      */
      if (!AppConfig.data.DisplayDeliveryRestaurantListScreen && skipTo) {
        dispatch(
          AddDeliveryOption(dl, {
            type: 'skip',
            to: skipTo
          })
        );
      } else {
        dispatch(
          AddDeliveryOption(dl, {
            type: 'choice',
            of: restaurants
          })
        );
      }

      restaurants.forEach((r) => {
        const dispatchThunk = dispatch as any;
        dispatchThunk(LoadDeliveryDetails(r.VirtualRestaurantId, dl.DeliveryLocationId));
      });
    });
  };
};

export const ClearDeliveryLocations = () => {
  return {
    type: 'CLEAR_DELIVERY_LOCATIONS'
  };
};

export const LoadDeliveryLocations = () => {
  // Thunk
  return (dispatch: Redux.Dispatch<Redux.Action>, getState: () => Store.State) => {
    const { flipdish, deliveryOptions } = getState();

    const deviceId = flipdish.Config.DeviceId;

    //Work with cached version
    const deliveryLocations = localStorage.getItem('flipdish-delivery-locations');

    if (deliveryLocations) {
      const deliveryLocationsJson = JSON.parse(deliveryLocations);

      dispatch({
        type: 'LOAD_DELIVERY_LOCATIONS',
        payload: deliveryLocationsJson
      });

      // TODO - maybe use a queue for this?
      const thunkDispatch = dispatch as any;
      deliveryLocationsJson.forEach((dl: any) => {
        const existing = deliveryOptions[dl.DeliveryLocationId];
        if (!existing || existing.status != 'success') {
          thunkDispatch(LoadDeliveryOptions(dl, true));
        }
      });
    }

    // TODO.marc - an error handling case here
    const query = DeliveryLocations.LoadPrevious(deviceId);

    query.then((d) => {
      // TODO: schema check
      // TODO: there are 2 'skip' params here that we need to deal with
      console.log('Loaded delivery locations', d.DeliveryLocations);

      Utilities.setInLocalStorage(
        'flipdish-delivery-locations',
        JSON.stringify(d.DeliveryLocations)
      );

      dispatch({
        type: 'LOAD_DELIVERY_LOCATIONS',
        payload: d.DeliveryLocations
      });

      // TODO - maybe use a queue for this?
      const thunkDispatch = dispatch as any;
      d.DeliveryLocations.forEach((dl) => {
        const existing = deliveryOptions[dl.DeliveryLocationId];
        if (!existing || existing.status != 'success') {
          thunkDispatch(LoadDeliveryOptions(dl, true));
        }
      });
    });

    query.catch((e) => {
      let appconfig: any = {};
      let customer: any = {};

      const flipdishAppConfig = getState().flipdish.AppConfig;
      const flipdishCustomer = getState().flipdish.Customer;

      if (flipdishAppConfig.status === 'success') {
        appconfig.AppNameId = flipdishAppConfig.data.AppNameId;
        appconfig.WhiteLabelId = flipdishAppConfig.data.WhiteLabelId;
      }

      if (flipdishCustomer.status === 'success') {
        customer.CustomerName = flipdishCustomer.data.CustomerName;
        customer.EmailAddress = flipdishCustomer.data.EmailAddress;
      }

      // sentry-disable-quota-overflow
      // Raven.captureMessage('Error loading past delivery locations', {
      //   tags: { category: 'API failure' },
      //   extra: {
      //     error: Utilities.handleError(e),
      //     appconfig: appconfig,
      //     customer: customer
      //   }
      // });
    });
  };
};

const SetCustomer = (payload: Data.Async<Api.Customer>): Store.SetCustomer => {
  switch (payload.status) {
    case 'success':
      return {
        type: 'SET_CUSTOMER',
        payload: {
          ...payload,
          data: {
            ...payload.data,
            // one day this will come from backend, maybe not here but...
            TipPercentageOptions: [0, 10, 20, 30]
          }
        }
      };
    default:
      return {
        type: 'SET_CUSTOMER',
        payload
      };
  }
};

export const SetCustomerName = (customerName: string) => {
  return (dispatch: Redux.Dispatch<Redux.Action>, getState: () => Store.State) => {
    console.log('Setting the customer name to ', customerName);
    axios.put('/Account/CustomerName', { customerName }).then(() => {
      // don't set the customer to 'pending' or reload all the data here.
      const loader = Utilities.toPromise<Api.Customer>(axios.get('/account/accountdetails'));
      loader.then((customer) => {
        Utilities.setInLocalStorage('flipdish-customer', JSON.stringify(customer));

        dispatch(
          SetCustomer({
            status: 'success',
            data: customer
          })
        );

        Intercom.Update(getState().intercom);
      });
    });
  };
};

export const SetCustomerEmail = (customerEmail: string) => {
  return (dispatch: Redux.Dispatch<Redux.Action>, getState: () => Store.State) => {
    console.log('Setting the customer email to ', customerEmail);
    axios
      .post('/Account/SetEmailAddress', { Email: customerEmail }, { withCredentials: true })
      .then(() => {
        // don't set the customer to 'pending' or reload all the data here.
        const loader = Utilities.toPromise<Api.Customer>(axios.get('/account/accountdetails'));
        loader.then((customer) => {
          Utilities.setInLocalStorage('flipdish-customer', JSON.stringify(customer));

          dispatch(
            SetCustomer({
              status: 'success',
              data: customer
            })
          );

          Intercom.Update(getState().intercom);
        });
      });
  };
};

export const UpdateCustomer = () => {
  return (dispatch: Redux.Dispatch<Redux.Action>) => {
    const loader = Utilities.toPromise<Api.Customer>(axios.get('/account/accountdetails'));

    loader.then((customer) => {
      Utilities.setInLocalStorage('flipdish-customer', JSON.stringify(customer));

      dispatch(
        SetCustomer({
          status: 'success',
          data: customer
        })
      );
    });
  };
};

export const LoadCustomer = (): ThunkResult<void> => (dispatch, getState) => {
  const thunkDispatch = dispatch as any;

  if (getState().flipdish.Customer.status === 'success') {
    return;
  }

  dispatch(SetCustomer({ status: 'pending' }));

  //Work with the cached version
  const customerFromStorage = localStorage.getItem('flipdish-customer');

  if (customerFromStorage) {
    const customerJson = JSON.parse(customerFromStorage);

    dispatch(
      SetCustomer({
        status: 'success',
        data: customerJson
      })
    );

    Intercom.Update(getState().intercom);

    thunkDispatch(LoadDeliveryLocations());

    const currentLanguageInStorage = localStorage.getItem('flipdish-current-language');

    const languages = getState().locale.languages;

    let isInLanguages = false;

    for (const language of languages) {
      if (currentLanguageInStorage === language.code) {
        isInLanguages = true;
        break;
      }
    }

    if (isInLanguages && currentLanguageInStorage) {
      thunkDispatch(setActiveLanguage(currentLanguageInStorage));
    } else {
      for (const language of languages) {
        if (language.active) {
          thunkDispatch(ChangeLanguage(language.code));
          break;
        }
      }
    }

    Raven.setExtraContext({ PhoneNumber: customerJson.PhoneNumber });
    Raven.setUserContext({
      email: customerJson.EmailAddress || undefined,
      username: customerJson.CustomerName || undefined,
      id: customerJson.UserId.toString()
    });
  }

  const loader = Utilities.toPromise<Api.Customer>(axios.get('/account/accountdetails'));

  loader.then((customer) => {
    Utilities.setInLocalStorage('flipdish-customer', JSON.stringify(customer));

    dispatch(
      SetCustomer({
        status: 'success',
        data: customer
      })
    );
    dispatch(userActions.loginSuccessBridge() as any);

    Intercom.Update(getState().intercom);

    thunkDispatch(LoadDeliveryLocations());

    const languages = getState().locale.languages;

    let isInLanguages = false;

    for (const language of languages) {
      if (customer.Language === language.code) {
        isInLanguages = true;
        break;
      }
    }

    if (isInLanguages) {
      thunkDispatch(setActiveLanguage(customer.Language));
      axios.defaults.headers.common['Flipdish-Language'] = customer.Language;

      Utilities.setInLocalStorage('flipdish-current-language', customer.Language);
    } else {
      for (const language of languages) {
        if (language.active) {
          thunkDispatch(ChangeLanguage(language.code));
          break;
        }
      }
    }
  });

  loader.catch(() => {
    thunkDispatch(LoadDeliveryLocations());

    dispatch(
      SetCustomer({
        status: 'none'
      })
    );
    dispatch(userActions.loginErrorBridge() as any);
  });
};

export const SetConsentPermission = (consent: boolean) => {
  return (dispatch: Redux.Dispatch<any>, getState: () => Store.State) => {
    const param = consent ? 'true' : 'false';
    axios.put('/Account/consent/' + param).then(() => {
      // don't set the customer to 'pending' or reload all the data here.
      const loader = Utilities.toPromise<Api.Customer>(axios.get('/account/accountdetails'));
      loader.then((customer) => {
        Utilities.setInLocalStorage('flipdish-customer', JSON.stringify(customer));

        dispatch(
          SetCustomer({
            status: 'success',
            data: customer
          })
        );

        Intercom.Update(getState().intercom);
      });
    });
  };
};

export const LoadPhoneNumberConfig = (coordinates: Api.Geo) => {
  return (dispatch: Redux.Dispatch<any>) => {
    const p = Utilities.toPromise<Api.PhoneNumberConfig>(
      axios.get('/Account/PhoneNumberConfig', {
        params: {
          Latitude: coordinates.Latitude,
          Longitude: coordinates.Longitude
        }
      })
    );

    p.then((config) => {
      const action: Store.LoadPhoneNumberConfig = {
        type: 'LOAD_PHONE_NUMBER_CONFIG',
        payload: config
      };
      dispatch(action);
    });
  };
};

export default AddToast;
